/**
 * Update Role Use Case
 *
 * Handles role updates with business logic validation.
 * Provides extension points for:
 * - Permission validation
 * - Change impact analysis (how many users affected)
 * - Auditing
 * - Event publishing (role.updated)
 *
 * @module domains/personnel/application/use-cases/UpdateRoleUseCase
 */

import type { IRoleApiPort } from '@personnel/application/ports/IRoleApiPort';
import type { RoleInputDto } from '@personnel/application/dtos/RoleInputDto';
import type { Role } from '@personnel/domain/entities/Role';

/**
 * Input data for updating a role
 */
export interface UpdateRoleInput {
  /** Role UUID to update */
  uuid: string;
  /** Role input data (full update) */
  data: RoleInputDto;
}

/**
 * Creates a use case to update an existing role
 *
 * @param props - Dependencies
 * @param props.apiPort - Role API port implementation
 * @returns Use case with execute method
 *
 * @example
 * const apiPort = createRoleHttpClient({ httpClient: connectApi });
 * const useCase = updateRoleUseCase({ apiPort });
 * const updated = await useCase.execute({
 *   uuid: 'role-123',
 *   data: { name: 'Senior Manager' }
 * });
 */
export const updateRoleUseCase = (props: { apiPort: IRoleApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes role update
   *
   * @param input - Update input with role UUID and data
   * @returns Promise resolving to updated Role entity
   * @throws Error if role not found or validation fails
   *
   * @example
   * const role = await execute({
   *   uuid: 'abc-123',
   *   data: { permissions: ['org-menu'] }
   * });
   */
  const execute = async (input: UpdateRoleInput): Promise<Role> => {
    const { uuid, data } = input;

    // Future business logic could go here:
    // - Validate user has permission to update roles
    // - Check if role is system role (can't be modified)
    // - Validate permission changes won't break user access
    // - Analyze impact (how many users affected)
    // - Log audit trail
    // - Publish domain events

    const role = await apiPort.updateRole(uuid, data);

    return role;
  };

  return {
    execute,
  };
};
